import { createContext } from 'react';
import type { UserSyncSettings } from '@/types/supabase/UserSyncSettings';

/**
 * UserSyncSettingsContext - Global User Sync Settings State
 *
 * Provides cached user sync settings across the application to avoid
 * duplicate fetches on every component mount.
 *
 * Architecture:
 * - Loaded once in PrivateAppProvider when user authenticates
 * - Subscribes to realtime updates for cross-device sync
 * - All components use useUserSyncSettings() hook to access cached settings
 * - Resets to null on sign-out (provider unmounts with user)
 *
 * @example
 * // In component:
 * const { settings, isLoading } = useUserSyncSettings();
 * if (isLoading || !settings) return null;
 * // Use settings.activeSyncProvider, settings.syncEnabled, etc.
 */

/**
 * Context value type for user sync settings
 */
export interface UserSyncSettingsContextValue {
  /** Loaded user sync settings, null if not yet loaded or load failed */
  settings: UserSyncSettings | null;
  /** Whether settings are currently being loaded */
  isLoading: boolean;
}

/**
 * Context with null default - requires provider to be mounted
 */
export const UserSyncSettingsContext = createContext<UserSyncSettingsContextValue | null>(null);